'use client'

import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import clsx from 'clsx'
import { motion, useReducedMotion } from 'framer-motion'

import { GooglePlacesLoader } from '@/lib/constants'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

import styles from './QuizAddressAutocomplete.module.css'

const DEFAULT_MINIMUM_QUERY_LENGTH = 3
const PREDICTION_STAGGER_SECONDS = 0.07
const MAX_STAGGERED_PREDICTIONS = 6

export type QuizAddressPrediction = {
  placeId: string
  description: string
  mainText: string
  secondaryText: string
  matchedSubstrings?: Array<{
    offset: number
    length: number
  }>
  toPlace?: () => QuizAddressPlace
}

export type QuizAddressPlace = {
  formattedAddress?: string | null
  addressComponents?: readonly unknown[] | null
  fetchFields: (options: { fields: string[] }) => Promise<unknown>
}

export type QuizResolvedAddress = {
  formattedAddress: string
  suburb: string
  state: string
  postcode: string
}

export type QuizAddressPredictionSource = (
  input: string,
) => Promise<QuizAddressPrediction[]>

export type QuizAddressAutocompleteProps = {
  id?: string
  name?: string
  label?: string
  value?: string
  defaultValue?: string
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  defaultOpen?: boolean
  debounceMilliseconds?: number
  minimumQueryLength?: number
  predictionSource?: QuizAddressPredictionSource
  emptyTitle?: string
  emptyDescription?: string
  manualEntryLabel?: string
  className?: string
  onValueChange?: (value: string) => void
  onSelect?: (prediction: QuizAddressPrediction) => void
  onManualEntry?: (value: string) => void
}

export async function resolveQuizAddressPrediction(
  prediction: QuizAddressPrediction,
): Promise<QuizResolvedAddress> {
  const fallback = parseQuizAddressPrediction(prediction)
  const place = prediction.toPlace?.()

  if (!place) return fallback

  try {
    await place.fetchFields({
      fields: ['formattedAddress', 'addressComponents'],
    })
    return parsePlaceAddress(place, prediction)
  } catch {
    return fallback
  }
}

export function parseQuizAddressPrediction(
  prediction: QuizAddressPrediction,
): QuizResolvedAddress {
  const normalizedSecondaryText = prediction.secondaryText
    .replace(/,?\s*Australia$/i, '')
    .replace(/,\s*/g, ' ')
    .trim()
  const match = normalizedSecondaryText.match(
    /^(.*)\s+(ACT|NSW|NT|QLD|SA|TAS|VIC|WA)\s+(\d{4})$/,
  )

  return {
    formattedAddress: prediction.mainText || prediction.description,
    suburb: match?.[1] ?? '',
    state: match?.[2] ?? '',
    postcode: match?.[3] ?? '',
  }
}

function parsePlaceAddress(
  place: Pick<QuizAddressPlace, 'formattedAddress' | 'addressComponents'>,
  prediction: QuizAddressPrediction,
): QuizResolvedAddress {
  const fallback = parseQuizAddressPrediction(prediction)
  let suburb = fallback.suburb
  let state = fallback.state
  let postcode = fallback.postcode
  let subpremise = ''
  let streetNumber = ''
  let route = ''

  for (const rawComponent of place.addressComponents ?? []) {
    const component = rawComponent as {
      types?: string[]
      shortText?: string
      longText?: string
      short_name?: string
      long_name?: string
    }
    const shortValue = component.shortText ?? component.short_name ?? ''
    const longValue = component.longText ?? component.long_name ?? shortValue

    if (
      component.types?.some((type) =>
        ['locality', 'postal_town', 'sublocality_level_1'].includes(type),
      )
    ) {
      suburb = longValue
    }
    if (component.types?.includes('administrative_area_level_1')) {
      state = shortValue
    }
    if (component.types?.includes('postal_code')) postcode = shortValue
    if (component.types?.includes('subpremise')) subpremise = longValue
    if (component.types?.includes('street_number')) streetNumber = longValue
    if (component.types?.includes('route')) route = longValue
  }

  const streetAddress = [streetNumber, route].filter(Boolean).join(' ')
  const formattedAddress =
    subpremise && streetAddress
      ? `${subpremise}/${streetAddress}`
      : streetAddress || fallback.formattedAddress

  return { formattedAddress, suburb, state, postcode }
}

function stripAustralia(value: string) {
  return value.replace(/,? Australia$/, '')
}

type GoogleFormattableText = {
  text: string
  matches: Array<{
    startOffset: number
    endOffset: number
  }>
  toString: () => string
}

type GooglePlacePrediction = {
  placeId: string
  text: GoogleFormattableText
  mainText?: GoogleFormattableText
  secondaryText?: GoogleFormattableText
  toPlace: () => QuizAddressPlace
}

type GoogleLegacyPrediction = {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text?: string
    main_text_matched_substrings?: Array<{
      offset: number
      length: number
    }>
  }
}

type GoogleLegacyPlaceResult = {
  formatted_address?: string | null
  address_components?: readonly unknown[] | null
}

type GoogleAutocompleteDataLibrary = {
  Place?: new (options: {
    id: string
    requestedLanguage?: string
    requestedRegion?: string
  }) => QuizAddressPlace
  AutocompleteSessionToken?: new () => object
  AutocompleteSuggestion?: {
    fetchAutocompleteSuggestions: (request: {
      input: string
      includedPrimaryTypes: string[]
      includedRegionCodes: string[]
      language: string
      region: string
      sessionToken: object
    }) => Promise<{
      suggestions: Array<{
        placePrediction?: GooglePlacePrediction
      }>
    }>
  }
  AutocompleteService?: new () => {
    getPlacePredictions: (request: {
      input: string
      types: string[]
      componentRestrictions: { country: string }
    }) => Promise<{ predictions: GoogleLegacyPrediction[] }>
  }
  PlacesService?: new (container: HTMLDivElement) => {
    getDetails: (
      request: { placeId: string; fields: string[] },
      callback: (
        result: GoogleLegacyPlaceResult | null,
        status: string,
      ) => void,
    ) => void
  }
}

async function fetchLegacyGoogleAddressPlace(
  library: GoogleAutocompleteDataLibrary,
  placeId: string,
) {
  const container = document.getElementById('gmap-hidden') as HTMLDivElement

  if (!library.PlacesService || !container) {
    throw new Error('Google Places details are unavailable')
  }

  return new Promise<GoogleLegacyPlaceResult>((resolve, reject) => {
    new library.PlacesService!(container).getDetails(
      {
        placeId,
        fields: ['formatted_address', 'address_components'],
      },
      (result, status) => {
        if (status === 'OK' && result) {
          resolve(result)
          return
        }

        reject(new Error(`Google Places details failed with ${status}`))
      },
    )
  })
}

function createGoogleAddressPlace(
  library: GoogleAutocompleteDataLibrary,
  placeId: string,
  modernPlace?: QuizAddressPlace,
): QuizAddressPlace {
  let legacyPlace: GoogleLegacyPlaceResult | undefined

  return {
    get formattedAddress() {
      return modernPlace?.formattedAddress ?? legacyPlace?.formatted_address
    },
    get addressComponents() {
      return modernPlace?.addressComponents ?? legacyPlace?.address_components
    },
    async fetchFields(options) {
      if (modernPlace) {
        try {
          const result = await modernPlace.fetchFields(options)
          if (modernPlace.addressComponents?.length) return result
        } catch {
          // Some projects still use the legacy Places Details API.
        }
      }

      legacyPlace = await fetchLegacyGoogleAddressPlace(library, placeId)
      return legacyPlace
    },
  }
}

export function createGoogleAddressPredictionSource() {
  let sessionToken: object | undefined

  return {
    load: async (input: string): Promise<QuizAddressPrediction[]> => {
      const library = (await GooglePlacesLoader.importLibrary(
        'places',
      )) as unknown as GoogleAutocompleteDataLibrary

      if (library.AutocompleteSuggestion && library.AutocompleteSessionToken) {
        sessionToken ??= new library.AutocompleteSessionToken()

        try {
          const { suggestions } =
            await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input,
              includedPrimaryTypes: [
                'street_address',
                'premise',
                'subpremise',
                'route',
              ],
              includedRegionCodes: ['au'],
              language: 'en-AU',
              region: 'au',
              sessionToken,
            })

          return suggestions.flatMap((suggestion) => {
            const prediction = suggestion.placePrediction
            if (!prediction) return []

            const mainText = prediction.mainText?.text ?? prediction.text.text
            const secondaryText = prediction.secondaryText?.text ?? ''

            return [
              {
                placeId: prediction.placeId,
                description: stripAustralia(prediction.text.toString()),
                mainText,
                secondaryText: stripAustralia(secondaryText),
                matchedSubstrings: prediction.mainText?.matches.map(
                  (match) => ({
                    offset: match.startOffset,
                    length: match.endOffset - match.startOffset,
                  }),
                ),
                toPlace: () =>
                  createGoogleAddressPlace(
                    library,
                    prediction.placeId,
                    prediction.toPlace(),
                  ),
              },
            ]
          })
        } catch {
          sessionToken = undefined
        }
      }

      if (!library.AutocompleteService) {
        throw new Error('Google Places autocomplete is unavailable')
      }

      const { predictions } =
        await new library.AutocompleteService().getPlacePredictions({
          input,
          types: ['address'],
          componentRestrictions: { country: 'au' },
        })

      return predictions.map((prediction) => ({
        placeId: prediction.place_id,
        description: stripAustralia(prediction.description),
        mainText: prediction.structured_formatting.main_text,
        secondaryText: stripAustralia(
          prediction.structured_formatting.secondary_text ?? '',
        ),
        matchedSubstrings:
          prediction.structured_formatting.main_text_matched_substrings,
        toPlace: () =>
          createGoogleAddressPlace(
            library,
            prediction.place_id,
            library.Place
              ? new library.Place({
                  id: prediction.place_id,
                  requestedLanguage: 'en-AU',
                  requestedRegion: 'au',
                })
              : undefined,
          ),
      }))
    },
    resetSession: () => {
      sessionToken = undefined
    },
  }
}

export async function loadGoogleAddressPredictions(
  input: string,
): Promise<QuizAddressPrediction[]> {
  return createGoogleAddressPredictionSource().load(input)
}

function getFallbackMatch(mainText: string, query: string) {
  const normalizedQuery = query.trim()
  if (!normalizedQuery) return []

  const offset = mainText
    .toLocaleLowerCase()
    .indexOf(normalizedQuery.toLocaleLowerCase())

  return offset < 0 ? [] : [{ offset, length: normalizedQuery.length }]
}

function MatchedMainText({
  prediction,
  query,
}: {
  prediction: QuizAddressPrediction
  query: string
}) {
  const matches = (
    prediction.matchedSubstrings?.length
      ? prediction.matchedSubstrings
      : getFallbackMatch(prediction.mainText, query)
  )
    .filter((match) => match.length > 0)
    .sort((a, b) => a.offset - b.offset)

  if (matches.length === 0) return prediction.mainText

  const content: ReactNode[] = []
  let cursor = 0

  matches.forEach((match, index) => {
    const start = Math.max(cursor, match.offset)
    const end = Math.min(
      prediction.mainText.length,
      Math.max(start, match.offset + match.length),
    )

    if (start > cursor) {
      content.push(prediction.mainText.slice(cursor, start))
    }

    if (end > start) {
      content.push(
        <strong key={`${start}-${end}-${index}`} className="font-bold">
          {prediction.mainText.slice(start, end)}
        </strong>,
      )
    }

    cursor = end
  })

  if (cursor < prediction.mainText.length) {
    content.push(prediction.mainText.slice(cursor))
  }

  return content
}

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-[14px] w-[14px] shrink-0 sm:h-[15px] sm:w-[15px]"
      fill="none"
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path
        d="m21 21-4.35-4.35"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

function LoadingIndicator() {
  return (
    <div className="flex items-center gap-3 px-3 py-[11px] sm:px-3.5 sm:py-3">
      <span className={styles.spinner} aria-hidden="true" />
      <span className="text-[13px] leading-[17px] text-[#82828B]">
        Searching addresses…
      </span>
    </div>
  )
}

export default function QuizAddressAutocomplete({
  id,
  name,
  label = 'Address',
  value,
  defaultValue = '',
  placeholder = 'Start typing your address',
  disabled = false,
  autoFocus = false,
  defaultOpen = false,
  debounceMilliseconds = 220,
  minimumQueryLength = DEFAULT_MINIMUM_QUERY_LENGTH,
  predictionSource,
  emptyTitle = 'No addresses found',
  emptyDescription = 'New build or unlisted? Add it yourself.',
  manualEntryLabel = 'Type my address',
  className,
  onValueChange,
  onSelect,
  onManualEntry,
}: QuizAddressAutocompleteProps) {
  const generatedId = useId()
  const prefersReducedMotion = useReducedMotion()
  const inputId = id ?? `${generatedId}-input`
  const panelId = `${inputId}-panel`
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue)
  const query = value ?? internalValue
  const debouncedQuery = useDebounce(query.trim(), debounceMilliseconds)
  const [predictions, setPredictions] = useState<QuizAddressPrediction[]>([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isOpen, setIsOpen] = useState(
    defaultOpen && query.trim().length >= minimumQueryLength,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadError, setHasLoadError] = useState(false)
  const requestId = useRef(0)
  const selectedDescription = useRef<string | null>(null)
  const googlePredictionSource = useMemo(
    createGoogleAddressPredictionSource,
    [],
  )
  const resolvedPredictionSource =
    predictionSource ?? googlePredictionSource.load
  const showGoogleAttribution = predictionSource === undefined

  const hasEnoughInput = query.trim().length >= minimumQueryLength
  const shouldShowPanel = isOpen && hasEnoughInput

  useEffect(() => {
    if (disabled) setIsOpen(false)
  }, [disabled])

  useEffect(() => {
    const normalizedQuery = debouncedQuery.trim()
    const currentRequestId = ++requestId.current

    if (
      normalizedQuery.length < minimumQueryLength ||
      selectedDescription.current === normalizedQuery
    ) {
      setIsLoading(false)
      setHasLoadError(false)
      if (normalizedQuery.length < minimumQueryLength) {
        setPredictions([])
        setActiveIndex(-1)
      }
      return
    }

    setIsLoading(true)
    setHasLoadError(false)

    resolvedPredictionSource(normalizedQuery)
      .then((nextPredictions) => {
        if (requestId.current !== currentRequestId) return

        setPredictions(nextPredictions)
        setActiveIndex(nextPredictions.length > 0 ? 0 : -1)
      })
      .catch(() => {
        if (requestId.current !== currentRequestId) return

        setPredictions([])
        setActiveIndex(-1)
        setHasLoadError(true)
      })
      .finally(() => {
        if (requestId.current === currentRequestId) setIsLoading(false)
      })

    return () => {
      if (requestId.current === currentRequestId) requestId.current += 1
    }
  }, [debouncedQuery, minimumQueryLength, resolvedPredictionSource])

  function updateValue(nextValue: string) {
    if (!isControlled) setInternalValue(nextValue)
    onValueChange?.(nextValue)
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const nextValue = event.target.value
    selectedDescription.current = null
    updateValue(nextValue)

    if (nextValue.trim().length >= minimumQueryLength) {
      setIsLoading(true)
      setHasLoadError(false)
      setIsOpen(true)
    } else {
      if (!predictionSource && nextValue.trim().length === 0) {
        googlePredictionSource.resetSession()
      }
      setIsLoading(false)
      setHasLoadError(false)
      setIsOpen(false)
      setPredictions([])
      setActiveIndex(-1)
    }
  }

  function handleSelect(prediction: QuizAddressPrediction) {
    selectedDescription.current = prediction.description.trim()
    updateValue(prediction.description)
    setIsOpen(false)
    if (!predictionSource) googlePredictionSource.resetSession()
    onSelect?.(prediction)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault()
      setIsOpen(false)
      return
    }

    if (predictions.length === 0 || isLoading) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex((current) =>
        !isOpen
          ? Math.max(current, 0)
          : current < predictions.length - 1
            ? current + 1
            : 0,
      )
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex((current) =>
        !isOpen
          ? predictions.length - 1
          : current > 0
            ? current - 1
            : predictions.length - 1,
      )
    } else if (event.key === 'Home' && isOpen) {
      event.preventDefault()
      setActiveIndex(0)
    } else if (event.key === 'End' && isOpen) {
      event.preventDefault()
      setActiveIndex(predictions.length - 1)
    } else if (event.key === 'Enter' && isOpen && activeIndex >= 0) {
      event.preventDefault()
      handleSelect(predictions[activeIndex])
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    const nextTarget = event.relatedTarget
    if (
      nextTarget instanceof Node &&
      event.currentTarget.contains(nextTarget)
    ) {
      return
    }

    setIsOpen(false)
  }

  const hasVisiblePredictions =
    shouldShowPanel && !isLoading && predictions.length > 0
  const shouldStaggerPredictions =
    predictions.length <= MAX_STAGGERED_PREDICTIONS
  const activeOptionId =
    hasVisiblePredictions && activeIndex >= 0
      ? `${panelId}-option-${activeIndex}`
      : undefined

  return (
    <div
      className={clsx(
        'w-full max-w-[680px] font-sans text-[#0B0B0F]',
        className,
      )}
      onBlur={handleBlur}
    >
      <label
        htmlFor={inputId}
        className="mb-[7px] block text-[13px] font-semibold leading-4 sm:mb-2 sm:text-[13.5px] sm:leading-[17px]"
      >
        {label}
      </label>

      <div className="relative">
        <div
          className={clsx(
            'flex h-[50px] items-center gap-2.5 rounded-[14px] border-[1.5px] border-[#0B0B0F] bg-white px-4 transition-[box-shadow,opacity] duration-200 ease-out sm:h-[52px] sm:px-[16.5px]',
            'focus-within:shadow-[0_0_0_3px_rgba(11,11,15,0.1)]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <SearchIcon />
          <input
            id={inputId}
            name={name}
            type="search"
            role="combobox"
            aria-activedescendant={activeOptionId}
            aria-autocomplete="list"
            aria-controls={hasVisiblePredictions ? panelId : undefined}
            aria-expanded={hasVisiblePredictions}
            aria-haspopup="listbox"
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder={placeholder}
            spellCheck={false}
            value={query}
            className={clsx(
              styles.input,
              'h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] leading-[19px] text-[#0B0B0F] outline-none placeholder:text-[#A0A0A8] focus:border-0 focus:ring-0 disabled:cursor-not-allowed',
            )}
            onChange={handleChange}
            onFocus={() => {
              if (hasEnoughInput) setIsOpen(true)
            }}
            onKeyDown={handleKeyDown}
          />
        </div>

        {shouldShowPanel && (
          <motion.div
            aria-busy={isLoading}
            className={clsx(
              'absolute left-0 top-full z-50 mt-2 w-full overflow-hidden rounded-[18px] border border-[#ECECEF] bg-white shadow-[0_20px_50px_rgba(11,11,15,0.16)] sm:rounded-[20px] sm:shadow-[0_24px_60px_rgba(11,11,15,0.14)]',
            )}
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : {
                    duration: softMotionDuration.panel,
                    ease: softEditorialEase,
                  }
            }
          >
            {isLoading ? (
              <div role="status" aria-live="polite" className="p-1.5 sm:p-2">
                <LoadingIndicator />
              </div>
            ) : predictions.length > 0 ? (
              <>
                <div
                  id={panelId}
                  role="listbox"
                  aria-label={`${label} suggestions`}
                  className="max-h-[246px] overflow-y-auto p-1.5 sm:max-h-[262px] sm:p-2"
                >
                  {predictions.map((prediction, index) => {
                    const isActive = index === activeIndex

                    return (
                      <motion.div
                        key={prediction.placeId}
                        id={`${panelId}-option-${index}`}
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive}
                        className={clsx(
                          styles.option,
                          'cursor-pointer select-none rounded-xl px-3 py-[11px] outline-none sm:px-3.5 sm:py-3',
                          isActive && 'bg-[#F4F4F6]',
                        )}
                        initial={
                          prefersReducedMotion ? false : { opacity: 0, y: 8 }
                        }
                        animate={{ opacity: 1, y: 0 }}
                        transition={
                          prefersReducedMotion
                            ? { duration: 0 }
                            : {
                                delay: shouldStaggerPredictions
                                  ? index * PREDICTION_STAGGER_SECONDS
                                  : 0,
                                duration: softMotionDuration.handoff,
                                ease: softEditorialEase,
                              }
                        }
                        onClick={() => handleSelect(prediction)}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActiveIndex(index)}
                      >
                        <div className="truncate text-[14px] font-medium leading-[18px] sm:text-[14.5px] sm:leading-[19px]">
                          <MatchedMainText
                            prediction={prediction}
                            query={query}
                          />
                        </div>
                        {prediction.secondaryText && (
                          <div className="mt-0.5 truncate text-[12.5px] leading-4 text-[#82828B]">
                            {prediction.secondaryText}
                          </div>
                        )}
                      </motion.div>
                    )
                  })}
                </div>
                {showGoogleAttribution && (
                  <div className="border-t border-[#ECECEF] px-3.5 py-2 text-right text-xs font-medium leading-4 text-[#5F6368]">
                    Google Maps
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-3.5 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:px-[22px] sm:py-[18px]">
                <div className="min-w-0" role="status" aria-live="polite">
                  <p className="text-[14.5px] font-semibold leading-[19px]">
                    {hasLoadError
                      ? 'Addresses are unavailable right now'
                      : emptyTitle}
                  </p>
                  <p className="mt-0.5 text-[13px] leading-[17px] text-[#82828B]">
                    {hasLoadError
                      ? 'You can still add your address yourself.'
                      : emptyDescription}
                  </p>
                </div>

                {onManualEntry && (
                  <button
                    type="button"
                    className="flex h-11 shrink-0 items-center justify-center rounded-full bg-[#F4F4F6] px-5 text-[14px] font-semibold leading-[18px] transition-colors duration-300 ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:bg-[#E8E8EB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B0B0F] focus-visible:ring-offset-2 active:bg-[#DEDEE2] sm:h-10 sm:text-[13.5px] sm:leading-[17px]"
                    onClick={() => {
                      if (!predictionSource) {
                        googlePredictionSource.resetSession()
                      }
                      onManualEntry(query)
                    }}
                  >
                    {manualEntryLabel}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}
